import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { coreLoginClient } from 'app/api/apiClient/generated/coreLoginClient';
import {
  areCookiesAccepted,
  getCompanyId,
  getHasVisitedWithinThirtyMinutes,
  getTokenExpFromResponse,
  hasUserInteractedWithinMinutes,
  isTokenLifeBeyondThreshold,
  isTokenMinutesFromExpiring,
  setHasVisitedWithinThirtyMinutes,
  setTokenExp,
} from 'app/api/cookie';
import {
  useExchangeToken,
  useGetIsTokenExpired,
  useLogout,
  useQueryUser,
} from 'app/api/hooks/core/v1/auth/user';
import LoadingScreen from 'app/pages/shared/LoadingScreen';

import EndOfSessionWarning from './EndOfSessionWarning';

// Component Definition
const SessionChecker: FC<PropsWithChildren> = ({ children }) => {
  const {
    data: userData,
    isInitialLoading: isLoadingUser,
  } = useQueryUser();

  const user = userData?.user;
  const isTokenExpired = useGetIsTokenExpired();

  const { mutate: logout } = useLogout();

  const { mutate: exchangeToken } = useExchangeToken();

  const tokenPollIntervalRef = useRef<null | number>(null);
  const tokenPollHydrateIntervalRef = useRef<null | number>(null);
  const [nearEndOfSession, setNearEndOfSession] = useState(false);

  const handleLogout = useCallback(() => {
    logout(undefined);
  },
  [logout]);

  const keepSessionAlive = useCallback(() => {
    exchangeToken(undefined);
    setNearEndOfSession(false);
  },
  [exchangeToken]);

  const stopIntervals = useCallback(() => {
    if (tokenPollIntervalRef.current) {
      clearInterval(tokenPollIntervalRef.current);
      tokenPollIntervalRef.current = null;
    }
    if (tokenPollHydrateIntervalRef.current) {
      clearInterval(tokenPollHydrateIntervalRef.current);
      tokenPollHydrateIntervalRef.current = null;
    }
  },
  []);

  const pollForToken = useCallback(() => {
    if (tokenPollIntervalRef.current) {
      return;
    }

    const interval = window.setInterval(async () => {
      const isNearEnd = isTokenMinutesFromExpiring();

      if (isNearEnd) {
        try {
          const tokenExpResponse = await coreLoginClient.tokenExpiration();

          setTokenExp(getTokenExpFromResponse(tokenExpResponse.data));

          if (tokenExpResponse.data.seconds <= 0) {
            handleLogout();
          }

          // It is possible the JWT Cookie was refreshed from another service or another open tab
          const isStillNearEnd = isTokenLifeBeyondThreshold(tokenExpResponse.data.seconds);
          const isIdle = !hasUserInteractedWithinMinutes(5);

          // if the token is about to expire and the user is idle
          if (isStillNearEnd && isIdle) {
            setNearEndOfSession(true);
            // if the user is not idle, do not even prompt them, just refresh the token for them
          } else if (isStillNearEnd && !isIdle) {
            keepSessionAlive();
          }
        } catch (_error) {
          handleLogout();
        }
      }
    },
    60000);
    tokenPollIntervalRef.current = interval;
  },
  [handleLogout, keepSessionAlive]);

  const onVisibilityChange = useCallback(() => {
    if (document.visibilityState !== 'visible') {
      return;
    }

    if (areCookiesAccepted() && !getHasVisitedWithinThirtyMinutes()) {
      window.location.reload();

      return;
    }

    setHasVisitedWithinThirtyMinutes();

    const companyId = getCompanyId();
    const userCompanyId = user?.company?.id;

    if (companyId && userCompanyId && userCompanyId !== companyId) {
      window.location.reload();
    }

    if (isTokenExpired) {
      stopIntervals();

      if (user) {
        window.location.reload();
      }
    }
  },
  [isTokenExpired, stopIntervals, user]);

  useEffect(() => {
    return () => {
      stopIntervals();
    };
  },
  [stopIntervals]);

  useEffect(() => {
    document.addEventListener('visibilitychange',
      onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange',
        onVisibilityChange);
    };
  },
  [onVisibilityChange]);

  useEffect(() => {
    if (user) {
      pollForToken();
    } else {
      stopIntervals();
    }
  },
  [user, pollForToken, stopIntervals]);

  // if we are fetching the user
  if (isLoadingUser) {
    return <LoadingScreen />;
  }

  // if we found a user
  if (user) {
    return (
      <>
        {nearEndOfSession && (
          <EndOfSessionWarning
            onKeepSessionAlive={keepSessionAlive}
            onLogout={handleLogout}
          />
        )}
        {children}
      </>
    );
  }

  // if a token is found and the user has not been fetched
  if (!isTokenExpired && !user) {
    return <LoadingScreen />;
  }

  return children;
};

export default SessionChecker;
